import type { ZodiosEndpointParameter } from '@zodios/core';
import { z, type ZodSchema } from 'zod';
import { cloneDeep, throttle as throttleFn } from 'lodash';
import type { SearchItem } from '@/components/extra/SearchBar.vue';
import { useEventBus } from '@vueuse/core';
import { useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/vue-query';
import { mergeObjectId } from '@/common';
import type { Endpoint, ExtractParametersOfType } from '@/api';
import type { PaginationQueryParams } from '@aka/vue-kit';
import { type MaybeRefOrGetter, toValue } from 'vue';

// #region Types & Schemas

type UnNested<T> = T extends { data: infer U } ? U : T;

type Single<T> = UnNested<T> extends (infer U)[] ? U : T;

type TData<Z extends ZodSchema> = UnNested<z.infer<Z>>;

const Paginated = z.object({
    current_page: z.number(),
    per_page: z.number(),
    total: z.number().nullish(),
    data: z.array(z.any()),
});

const Nested = z.object({
    data: z.array(z.any()).or(z.record(z.any())),
});

export type Props<P extends readonly ZodiosEndpointParameter[], Z extends ZodSchema> = {
    endpoint: Endpoint<P, Z>;
    params?: MaybeRefOrGetter<ExtractParametersOfType<'Path', P> | undefined>;
    queries?: MaybeRefOrGetter<Partial<ExtractParametersOfType<'Query', P>>>;
    debounce?: number;
    throttle?: number;
    delay?: number;
    entity?: string;
    refreshKey?: any;
    search?: SearchItem<P[number]>[];
    collect?: Record<string, (data: TData<Z>, payload: any) => undefined | null | false | TData<Z>>;
    resetOnRefresh?: boolean;
    paginated?: boolean;
    searchClasses?: any;
    queryKeyPrefix?: any[];
    invalidateQueries?: boolean;
    invalidateQueriesExtra?: any[][];
    queryOptions?: Omit<UseQueryOptions<z.infer<Z>>, 'queryKey' | 'queryFn' | 'enabled'>;
    deepMerge?: (keyof Partial<Single<TData<Z>>>)[];
};

// #endregion

export const useEndpointData = <P extends readonly ZodiosEndpointParameter[], Z extends ZodSchema>(
    props: Props<P, Z>,
) => {
    const isPaginated = computed(() =>
        props.endpoint.parameters?.some(p => p.name === 'page[size]' || p.name === 'page[number]'),
    );

    const hasPathParams = computed(() => props.endpoint.parameters?.some(p => p.type === 'Path'));

    // state
    const times = ref(0);
    const searchData = ref<Record<string, any>>({});
    const numberOrUndefined = (n: any): number | undefined => (typeof n === 'number' ? n : undefined);
    const paginator = usePagination(
        numberOrUndefined(toValue(props.queries as any)?.['page[size]']),
        numberOrUndefined(toValue(props.queries as any)?.['page[number]']) ?? 1,
    );

    const queryKey = eagerComputed(() => [
        ...(props.queryKeyPrefix ?? []),
        props.endpoint.alias,
        {
            params: toValue(props.params),
            queries: {
                ...toValue(props.queries),
                ...({
                    'page[size]': paginator.perPage.value,
                    'page[number]': paginator.page.value,
                } satisfies PaginationQueryParams),
                ...searchData.value,
            },
        },
        props.refreshKey,
    ]);

    const {
        data: result,
        error,
        isLoading,
        refetch,
    } = useQuery<z.infer<Z>>({
        queryKey,
        queryFn: async (ctx: any) => {
            if (props.delay && times.value === 0) {
                await new Promise(resolve => setTimeout(resolve, props.delay));
            }
            times.value = times.value + 1;
            return await (api as any)[props.endpoint.alias]?.(
                ctx.queryKey[(props.queryKeyPrefix?.length ?? 0) + 1] as any,
            );
        },
        enabled: computed(() => !hasPathParams.value || toValue(props.params) != null),
        ...props.queryOptions,
    });

    watch(
        result,
        resultData => {
            if (resultData && isPaginated.value) {
                paginator.tryUpdateFromResponse(resultData);
            }
        },
        { immediate: true },
    );

    const data = eagerComputed(() => {
        const nested = Nested.safeParse(result.value);
        if (nested.success) {
            return nested.data.data as TData<Z> | undefined;
        }
        return result.value as TData<Z> | undefined;
    });

    const queryClient = useQueryClient();
    // const _merge = useSmartMerge(data) as (data: Partial<Single<TData>>, isDelete?: boolean) => void;
    const merge = (dt: Partial<Single<TData<Z>>>, isDelete?: boolean, opts?: { removeDeep?: boolean }) => {
        dt = cloneDeep(dt);
        queryClient.setQueryData(queryKey.value, (old: any) => {
            let result;
            if (isPaginated.value) {
                const parse = Nested.safeParse(old);
                if (parse.success) {
                    result = {
                        ...parse.data,
                        data: mergeObjectId(dt, parse.data.data, isDelete, {
                            deep: props.deepMerge,
                            removeDeep: opts?.removeDeep,
                        }),
                    };
                } else {
                    result = mergeObjectId(dt, old, isDelete, {
                        deep: props.deepMerge,
                        removeDeep: opts?.removeDeep,
                    });
                }
            } else {
                result = mergeObjectId(dt, old, isDelete, {
                    deep: props.deepMerge,
                    removeDeep: opts?.removeDeep,
                });
            }

            return result;
        });
        if (props.invalidateQueries) {
            queryClient.invalidateQueries({ queryKey: queryKey.value });
        }
        if (props.invalidateQueriesExtra) {
            for (const queryKey of props.invalidateQueriesExtra) {
                queryClient.invalidateQueries({ queryKey });
            }
        }
    };

    const throttledRefetch = throttleFn(refetch, props.throttle ?? 250);

    //entity
    if (props.entity) {
        const { on } = useEntityEvents<Single<z.infer<Z>>>(props.entity);
        on('saved', (d: any) => {
            merge(d);
        });
        on('deleted', (d: any) => {
            merge(d, true);
        });
    }
    if (props.collect) {
        const { on } = useEventBus<{
            type: string;
            value: any;
        }>('entity-events');
        on((payload: any) => {
            if (payload.type in (props.collect ?? {}) && data.value != null) {
                const newData = props.collect?.[payload.type]?.(data.value, payload.value);
                if (newData) {
                    merge(newData);
                }
            }
        });
    }

    // search
    const mergeSearch = useSmartMerge(searchData);
    const updateSearch = (search: Record<string, any>) => {
        mergeSearch(search);
        refetch();
    };

    return {
        queryKey,
        data,
        result,
        isLoading,
        error,
        refetch,
        paginator,
        merge,
        throttledRefetch,
        mergeSearch,
        updateSearch,
    };
};
